import React, { useState, useEffect } from "react";
import { X, Shield } from "lucide-react";

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const accepted = localStorage.getItem("sb-cookies-accepted");
    if (!accepted) {
      const timer = setTimeout(() => setVisible(true), 3000);
      return () => clearTimeout(timer);
    }
  }, []);

  const accept = () => {
    localStorage.setItem("sb-cookies-accepted", "true");
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-4 bg-black/40 backdrop-blur-sm" data-testid="cookie-consent-overlay">
      <div
        className="w-full max-w-md bg-[#1a1a2e] border border-white/10 rounded-2xl p-6 shadow-2xl animate-in slide-in-from-bottom-4 duration-300"
        data-testid="cookie-consent-popup"
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#D4AF37]" />
            <h3 className="text-white font-bold text-sm">Navigazione sicura</h3>
          </div>
          <button onClick={accept} className="text-gray-500 hover:text-white transition-colors" data-testid="cookie-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <p className="text-gray-400 text-xs leading-relaxed mb-5">
          Per facilitare la navigazione e offrirti un'esperienza personalizzata, utilizziamo cookie tecnici e di analisi. Puoi accettare o accedere con il tuo account preferito.
        </p>

        {/* Social Login Buttons */}
        <div className="grid grid-cols-2 gap-2 mb-4">
          {/* Google */}
          <button
            onClick={accept}
            className="flex items-center justify-center gap-2 bg-white hover:bg-gray-100 text-gray-800 rounded-xl px-3 py-2.5 text-xs font-medium transition-colors"
            data-testid="cookie-login-google"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            Google
          </button>
          {/* Meta/Facebook */}
          <button
            onClick={accept}
            className="flex items-center justify-center gap-2 bg-[#1877F2] hover:bg-[#166FE5] text-white rounded-xl px-3 py-2.5 text-xs font-medium transition-colors"
            data-testid="cookie-login-meta"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="white">
              <path d="M12 2C6.477 2 2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.879V14.89h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.989C18.343 21.129 22 16.99 22 12c0-5.523-4.477-10-10-10z"/>
            </svg>
            Meta
          </button>
          {/* Amazon */}
          <button
            onClick={accept}
            className="flex items-center justify-center gap-2 bg-[#FF9900] hover:bg-[#e88c00] text-black rounded-xl px-3 py-2.5 text-xs font-medium transition-colors"
            data-testid="cookie-login-amazon"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
              <path d="M13.958 10.09c0 1.232.029 2.256-.591 3.351-.502.891-1.301 1.438-2.186 1.438-1.214 0-1.922-.924-1.922-2.292 0-2.692 2.415-3.182 4.7-3.182v.685zm3.186 7.705a.66.66 0 01-.753.077c-1.06-.878-1.25-1.284-1.828-2.119-1.748 1.784-2.986 2.317-5.249 2.317-2.681 0-4.764-1.655-4.764-4.967 0-2.585 1.401-4.345 3.393-5.204 1.726-.756 4.14-.891 5.985-1.1v-.41c0-.752.058-1.642-.384-2.292-.384-.579-1.117-.818-1.764-.818-1.199 0-2.266.615-2.527 1.89a.534.534 0 01-.462.462l-2.604-.28c-.218-.049-.46-.222-.398-.554C6.41 2.67 9.18 1.5 11.685 1.5c1.262 0 2.911.336 3.905 1.29 1.262 1.178 1.142 2.753 1.142 4.467v4.043c0 1.214.504 1.747.977 2.403.166.233.203.513-.01.687-.532.444-1.478 1.27-1.998 1.733l-.557-.328z"/>
              <path d="M21.533 18.119C18.97 20.04 15.197 21.062 11.934 21.062c-4.563 0-8.672-1.688-11.781-4.496-.244-.22-.026-.52.267-.35 3.355 1.953 7.504 3.127 11.789 3.127 2.89 0 6.065-.599 8.99-1.838.44-.189.81.29.334.614z"/>
            </svg>
            Amazon
          </button>
          {/* PayPal */}
          <button
            onClick={accept}
            className="flex items-center justify-center gap-2 bg-[#003087] hover:bg-[#002670] text-white rounded-xl px-3 py-2.5 text-xs font-medium transition-colors"
            data-testid="cookie-login-paypal"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="white">
              <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.771.771 0 0 1 .761-.652h6.199c2.063 0 3.543.585 4.268 1.694.335.516.538 1.087.617 1.742.083.677.032 1.478-.157 2.398l-.013.063v.564l.44.249c.372.21.67.455.893.735.37.464.607 1.042.7 1.715.096.693.037 1.498-.173 2.393-.241 1.028-.635 1.922-1.171 2.655a5.292 5.292 0 0 1-1.539 1.546c-.593.39-1.298.672-2.097.84-.769.161-1.64.245-2.582.245h-.614a1.793 1.793 0 0 0-1.77 1.513l-.046.244-.77 4.88-.036.174a.164.164 0 0 1-.047.1.13.13 0 0 1-.078.028z"/>
            </svg>
            PayPal
          </button>
        </div>

        {/* Accept all button */}
        <button
          onClick={accept}
          className="w-full bg-[#D4AF37] hover:bg-[#B8942E] text-black font-bold py-3 rounded-xl transition-colors text-sm"
          data-testid="cookie-accept-all"
        >
          Accetta e continua
        </button>

        <p className="text-gray-600 text-[10px] text-center mt-3">
          Continuando accetti la nostra{" "}
          <a href="/privacy" className="text-[#D4AF37] hover:underline">Privacy Policy</a>
          {" "}e i{" "}
          <a href="/privacy" className="text-[#D4AF37] hover:underline">Cookie</a>
        </p>
      </div>
    </div>
  );
}
